import { useEffect, useState } from "react"
import { InputAdornment, Stack, TextField, Typography } from "@mui/material"
import { confirm } from "lib/@dialogs/confirm"

export function FrequencyModifierInput({ multiplier, onChange }) {
    const initial = getSafeMultiplier(multiplier)
    const [localValue, setLocalValue] = useState(String(initial))

    useEffect(syncFromProp, [multiplier])

    return (
        <Stack direction="row" alignItems="center" spacing={1} data-cy="frequency-modifier-input">
            <TextField
                size="small"
                type="number"
                value={localValue}
                onChange={handleValueChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onWheel={handleWheel}
                inputProps={{ min: 0, step: 0.25 }}
                sx={{ width: 120 }}
                InputProps={{
                    endAdornment: <InputAdornment position="end">x</InputAdornment>,
                }}
            />
            <Typography variant="caption" color="text.secondary">
                {describeMultiplier(localValue)}
            </Typography>
        </Stack>
    )

    function syncFromProp() {
        setLocalValue(String(getSafeMultiplier(multiplier)))
    }

    function handleFocus(e) {
        e.target.select()
    }

    function handleWheel(e) {
        e.target.blur()
    }

    function handleValueChange(e) {
        setLocalValue(e.target.value)
    }

    async function handleBlur() {
        const numeric = parseFloat(localValue)
        await applyMultiplier(Number.isFinite(numeric) ? numeric : 0)
    }

    async function applyMultiplier(newMultiplier) {
        if (newMultiplier <= 0) {
            const reset = await confirm(
                "A frequency multiplier of 0 or below means matched tasks never run. Reset it to 1x (no change)? To remove tasks entirely, use the Exclude action.",
                "Invalid Frequency Modifier"
            )
            if (reset) {
                setLocalValue("1")
                onChange(1)
            } else {
                syncFromProp()
            }
            return
        }
        if (newMultiplier > 10) {
            const ok = await confirm(
                `A frequency multiplier of ${newMultiplier}x is very high. Are you sure?`,
                "High Frequency Modifier"
            )
            if (!ok) {
                syncFromProp()
                return
            }
        }
        onChange(newMultiplier)
    }
}

function getSafeMultiplier(multiplier) {
    if (Number.isFinite(multiplier) && multiplier > 0) return multiplier
    return 1
}

function describeMultiplier(value) {
    const multiplier = parseFloat(value)
    if (!Number.isFinite(multiplier) || multiplier <= 0) return "(invalid)"
    if (multiplier === 1) return "(no change)"
    if (multiplier > 1) return `${multiplier}x as often`
    return `${multiplier}x as often`
}
